import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, concat, EMPTY, from, map, Observable, of, switchMap, toArray, withLatestFrom } from 'rxjs';

import { WeeklySchedule } from '@core/models';
import { WeeklyScheduleApiService } from '@core/data/api/weekly-schedule-api.service';
import { activityToDto, categoryToDto, weeklyScheduleFromDto } from '@core/data/api/weekly-schedule.mapper';
import { emptyWeek } from '@core/util/weekly-schedule.util';
import { DataRepository } from '@core/data/data-repository';
import { selectCurrentPersona } from '@state/session/session.reducer';
import { ScheduleApiActions, SchedulePageActions } from './schedule.actions';

/**
 * Bridges Weekly Schedule actions to the API.
 *
 * WHY EVERY WRITE IS FOLLOWED BY A READ: a create comes back carrying a guid the
 * client did not choose, so an optimistic row has to be found and replaced.
 * Re-reading the week instead is one small GET on a document of a few dozen rows,
 * and it removes a whole class of reconciliation bug — including the one where an
 * overwrite's deletes land and its create does not.
 *
 * With no `apiUrl` configured everything falls back to the repository and the
 * reducer's own optimistic apply stands, which is what keeps the prototype
 * demoing without a backend.
 */
@Injectable()
export class ScheduleEffects {
  private readonly actions$ = inject(Actions);
  private readonly repo = inject(DataRepository);
  private readonly api = inject(WeeklyScheduleApiService);
  private readonly store = inject(Store);

  /**
   * On page enter, fetch the week; map to Load Success / Load Failure. The colour
   * palette its categories wear is app-wide reference data, loaded once by
   * `ReferenceEffects`, not here.
   */
  readonly load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulePageActions.entered),
      withLatestFrom(this.store.select(selectCurrentPersona)),
      switchMap(([, persona]) =>
        this.schedules$(persona?.id).pipe(
          map((schedules) => ScheduleApiActions.loadSuccess({ schedules })),
          catchError((err: unknown) => of(ScheduleApiActions.loadFailure({ error: toMessage(err) }))),
        ),
      ),
    ),
  );

  /**
   * The portfolio, for the people allowed to see it.
   *
   * It is its own access-matrix resource — admin and CEO only — so asking for it
   * as anybody else is a 403 and a dialog about something they never requested.
   * Fetched only when the viewer can have it.
   */
  readonly loadPortfolio$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulePageActions.entered),
      withLatestFrom(this.store.select(selectCurrentPersona)),
      switchMap(([, persona]) => {
        if (!this.api.available() || !(persona?.isCEO || persona?.isAdmin)) return EMPTY;
        return this.api.list().pipe(
          map((rows) => ScheduleApiActions.portfolioLoaded({ personaCodes: rows.map((r) => r.personaCode) })),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  /**
   * Save one activity, after removing whatever it overwrites.
   *
   * Strictly in sequence, deletes first: a create that landed before its deletes
   * would momentarily overlap them, which is exactly what the server rejects.
   */
  readonly saveActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulePageActions.activitiesSaved),
      switchMap(({ personaCode, removed, activity, created }) => {
        if (!this.api.available()) return EMPTY;
        const body = activityToDto(activity);
        const steps: Observable<unknown>[] = removed.map((guid) => this.api.deleteActivity(personaCode, guid));
        steps.push(
          created
            ? this.api.createActivity(personaCode, body)
            : this.api.updateActivity(personaCode, activity.activityGuid, body),
        );
        return this.afterWrite(personaCode, concat(...steps));
      }),
    ),
  );

  readonly removeActivity$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulePageActions.activityRemoved),
      switchMap(({ personaCode, activityGuid }) =>
        this.api.available()
          ? this.afterWrite(personaCode, this.api.deleteActivity(personaCode, activityGuid))
          : EMPTY,
      ),
    ),
  );

  readonly saveCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulePageActions.categorySaved),
      switchMap(({ personaCode, category, created }) => {
        if (!this.api.available()) return EMPTY;
        const body = categoryToDto(category);
        return this.afterWrite(
          personaCode,
          created
            ? this.api.createCategory(personaCode, body)
            : this.api.updateCategory(personaCode, category.categoryGuid, body),
        );
      }),
    ),
  );

  readonly removeCategory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulePageActions.categoryRemoved),
      switchMap(({ personaCode, categoryGuid }) =>
        this.api.available()
          ? this.afterWrite(personaCode, this.api.deleteCategory(personaCode, categoryGuid))
          : EMPTY,
      ),
    ),
  );

  /**
   * Run a write, then take the server's copy of the week as the truth.
   *
   * On failure nothing is dispatched: the error interceptor has already put the
   * server's own message in front of the user, and the optimistic row stays on
   * screen so whatever they typed is still there. The next page visit re-reads.
   */
  private afterWrite(personaCode: string, write: Observable<unknown>) {
    return write.pipe(
      // Waits for the write (or the whole sequence of them) to COMPLETE, and
      // discards what it emitted — the week is read back rather than assembled
      // from the pieces that came back one at a time.
      toArray(),
      switchMap(() => this.api.get(personaCode)),
      map((dto) => ScheduleApiActions.weekLoaded({ schedule: weeklyScheduleFromDto(dto) })),
      catchError((err: unknown) => this.resync(personaCode, err)),
    );
  }

  /**
   * A refused write, and what to do about the one that means we were wrong.
   *
   * A `409` on an activity is an overlap the client did not know about — its copy
   * of the week is behind, which is exactly the state in which it will keep
   * offering the same impossible save. So re-read. Anything else is reported by
   * the error interceptor and the optimistic row stays, so what the person typed
   * is still in front of them.
   */
  private resync(personaCode: string, err: unknown) {
    if (!(err instanceof HttpErrorResponse) || err.status !== 409) return EMPTY;
    return this.api.get(personaCode).pipe(
      map((dto) => ScheduleApiActions.weekLoaded({ schedule: weeklyScheduleFromDto(dto) })),
      catchError(() => EMPTY),
    );
  }

  /**
   * The current persona's week.
   *
   * A failed read is NOT fatal: having no week yet is the normal case for
   * somebody who has never opened the page.
   *
   * It answers an EMPTY week rather than nothing, which matters. Answering
   * nothing let the store fall back to `blankWeek`, and that carries seed
   * furniture — three categories with made-up ids like `cat-work`. Against a real
   * backend those are not categories at all: adding an activity against one
   * answered `'cat-work' is not a valid UUID`. An empty week has no categories to
   * pick, which is the truth, and the first one somebody adds is theirs with a
   * guid the server issued.
   */
  private schedules$(personaCode: string | undefined): Observable<WeeklySchedule[]> {
    if (!personaCode) return of([]);
    return this.api.get(personaCode).pipe(
      map((dto) => [weeklyScheduleFromDto(dto)]),
      catchError(() => of([emptyWeek(personaCode)])),
    );
  }
}

function toMessage(err: unknown): string {
  return err instanceof Error ? err.message : 'Unknown error';
}
